'use client';

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { Box, HStack, Image, Stack, Text, VStack } from '@chakra-ui/react';
import {
  CalendarMonth,
  ChevronLeft,
  ChevronRight,
  Directions,
  DirectionsWalk,
  EmojiEvents,
  FmdGood,
  Paid,
} from '@m/icons';

import { Badge } from '../badge';
import { Button } from '../button';
import { SectionHeader } from '../mobile-rows';
import { Tag } from '../tag';

import {
  HeroImageCarousel,
  listCardImageUrl,
  LIST_ZONE_PLACEHOLDER_URL,
  previewImageUrls,
} from './hero-image-carousel';
import { LocationTag } from './location-tag';

function eventBadgeIcon(badge: string) {
  const normalized = badge.trim().toLowerCase();
  if (normalized === 'best value') return <Paid />;
  if (normalized === 'nearest' || normalized === 'closest') return <FmdGood />;
  return undefined;
}

/**
 * "Event rate" with the date window on the next line when the window is known
 * (Figma "Event reservations results"), falling back to the generic
 * "Event rate applies" trophy badge otherwise (Figma "Reservations
 * results_Venue_Normal rates and event rates"). Rendered next to the walk-time
 * tag — never in place of it, and never in place of the price block below.
 */
function EventRateTag({ eventRateWindow }: { eventRateWindow?: string }) {
  if (eventRateWindow) {
    return (
      <Tag
        size="sm"
        h="auto"
        py="0.5"
        w="full"
        maxW="full"
        whiteSpace="normal"
        alignItems="flex-start"
        css={{
          '& .chakra-tag__label': {
            lineClamp: 'unset',
            whiteSpace: 'normal',
            display: 'block',
          },
        }}
      >
        Event rate
        <br />
        {eventRateWindow}
      </Tag>
    );
  }
  return (
    <Tag size="sm" startElement={<EmojiEvents />}>
      Event rate applies
    </Tag>
  );
}

const IMAGE_SIZE = 104;

export interface ReservationCardProps {
  /** Pill badge shown above the name in the `event` variant (e.g. "Best value", "Closest"). */
  badge?: string;
  /** href for the `event` variant's "Book now" button. Prefer this over onBookNow for server-rendered navigation. */
  bookNowHref?: string;
  /** "Book now" CTA label for the `event` variant. Defaults to "Book now". */
  bookNowLabel?: string;
  /** target for the "Book now" anchor (e.g. "_blank"). Defaults to same tab. */
  bookNowTarget?: string;
  /** Closing time (e.g. "2PM"). With closingTime, shows "Open • Until {closingTime}" */
  closingTime?: string;
  /** href for the details link. Prefer this over onDetailsClick for server-rendered navigation. */
  detailsHref?: string;
  detailsLabel?: string;
  /** target for the details anchor (e.g. "_blank" for new tab). Defaults to same tab. */
  detailsTarget?: string;
  directionsLabel?: string;
  /** Icon rendered before the directions/book label. */
  directionsStartElement?: ReactNode;
  /** When true, shows an event-rate tag (see `eventRateWindow`) next to the walk-time tag. */
  eventRateApplies?: boolean;
  /**
   * Formatted parking window for the active event rate, including the date
   * (e.g. "Oct 21, 6:00 PM – Oct 22, 12:00 AM").
   * When set alongside `eventRateApplies`, the tag shows "Event rate" with
   * `{eventRateWindow}` on the next line instead of the generic "Event rate applies".
   */
  eventRateWindow?: string;
  /** When true, shows the neutral map-focus background. Selected takes precedence. */
  focused?: boolean;
  imageAlt?: string;
  /** Tag rendered at the bottom of the image (e.g. "Closest") */
  imageTag?: string;
  imageUrl?: string;
  /** Ordered photo URLs. The card shows the first photo, or the mark when none exist. */
  imageUrls?: readonly string[];
  /** When true and isOpen, shows "Open 24 hrs" instead of a closing time */
  is24Hours?: boolean;
  /** Whether the location is currently open */
  isOpen?: boolean;
  locationName: string;
  onBookNow?: () => void;
  /** Compact directions icon button shown beside the primary action. */
  onCompactDirectionsClick?: () => void;
  onDetailsClick?: () => void;
  onDirectionsClick?: () => void;
  priceFormatted?: string;
  /** Rate label shown below the price (e.g. "up to 1 hour" or "per hour") */
  rateLabel?: string;
  /** When true, shows a "Reservations available" tag with a calendar icon. */
  reservationsAvailable?: boolean;
  selected?: boolean;
  /** Whether to show the directions button. Defaults to true. */
  showDirections?: boolean;
  /** Whether to show the image column. Defaults to true. */
  showImage?: boolean;
  /**
   * list: bottom border only, full padding (default). card: full border, rounded
   * corners, compact padding. event: rounded card (no border) with an optional
   * top badge, open/walk meta, and a "Book now" CTA (used on event landing pages).
   */
  variant?: 'list' | 'card' | 'event';
  /**
   * Walk time / distance line shown under the name (e.g. "8 min (0.3mi)"). Used by the `event`
   * variant and, in the `list`/`card` variants, takes the place of the open/closed status line.
   */
  walkLabel?: string;
}

export function ReservationCard({
  badge,
  bookNowLabel = 'Book now',
  bookNowHref,
  bookNowTarget,
  onBookNow,
  walkLabel,
  detailsLabel = 'Details',
  detailsHref,
  detailsTarget,
  directionsLabel = 'Directions',
  directionsStartElement,
  imageUrl,
  imageUrls,
  imageAlt = 'Parking location',
  imageTag,
  isOpen,
  is24Hours,
  closingTime,
  locationName,
  rateLabel,
  priceFormatted,
  onDetailsClick,
  onDirectionsClick,
  onCompactDirectionsClick,
  reservationsAvailable = false,
  eventRateApplies = false,
  eventRateWindow,
  focused = false,
  selected = false,
  showImage = true,
  showDirections = true,
  variant = 'list',
}: ReservationCardProps) {
  let background = 'fill.background.default';
  if (focused) background = 'fill.background.light-gray';
  if (selected) background = 'fill.background.banner';

  if (variant === 'event') {
    let detailsAction: ReactNode = null;
    if (detailsHref) {
      detailsAction = (
        <Text asChild variant="Link">
          <a
            href={detailsHref}
            target={detailsTarget}
            rel={detailsTarget === '_blank' ? 'noopener noreferrer' : undefined}
          >
            {detailsLabel}
          </a>
        </Text>
      );
    } else if (onDetailsClick) {
      detailsAction = (
        <Text as="button" variant="Link" onClick={onDetailsClick}>
          {detailsLabel}
        </Text>
      );
    }

    const bookNow = bookNowHref ? (
      <Button asChild variant="primary" size="sm">
        <a
          href={bookNowHref}
          target={bookNowTarget}
          rel={bookNowTarget === '_blank' ? 'noopener noreferrer' : undefined}
        >
          {bookNowLabel}
        </a>
      </Button>
    ) : (
      <Button variant="primary" size="sm" onClick={onBookNow}>
        {bookNowLabel}
      </Button>
    );

    let openLabel: string | undefined;
    if (isOpen == null) {
      openLabel = undefined;
    } else if (!isOpen) {
      openLabel = 'Closed';
    } else if (is24Hours) {
      openLabel = 'Open 24 hrs';
    } else if (closingTime) {
      openLabel = `Open • Until ${closingTime}`;
    } else {
      openLabel = 'Open';
    }

    return (
      <Box
        bg={background}
        border="1px solid"
        borderColor="stroke.divider.emphasized"
        borderRadius="2xl"
        p="4"
        w="full"
      >
        <Stack gap="2">
          {badge != null && (
            <Badge variant="brand" size="sm" icon={eventBadgeIcon(badge)}>
              {badge}
            </Badge>
          )}
          <HStack justify="space-between" align="flex-start" gap="4">
            <VStack align="flex-start" gap="2" flex={1} minW={0}>
              <Text variant="MainBodyMedium" color="text.primary" lineClamp={1}>
                {locationName}
              </Text>
              <HStack gap="4" align="center" flexWrap="wrap">
                {openLabel && (
                  <Text
                    variant="SmallBodyMedium"
                    color={isOpen ? 'text.discount' : 'text.secondary'}
                  >
                    {openLabel}
                  </Text>
                )}
                {walkLabel && (
                  <HStack gap="1" color="text.secondary" fontSize="sm">
                    <DirectionsWalk aria-hidden />
                    <Text variant="SmallBodyRegular">{walkLabel}</Text>
                  </HStack>
                )}
              </HStack>
            </VStack>
            {(priceFormatted || rateLabel) && (
              <VStack align="flex-end" gap="1" flexShrink={0}>
                {priceFormatted && (
                  <Text variant="MainBodyDemiBold" color="text.primary">
                    {priceFormatted}
                  </Text>
                )}
                {rateLabel && (
                  <Text variant="SmallBodyRegular" color="text.secondary">
                    {rateLabel}
                  </Text>
                )}
              </VStack>
            )}
          </HStack>
          {(reservationsAvailable || eventRateApplies) && (
            <HStack gap="2" flexWrap="wrap" alignSelf="stretch" w="full" minW={0}>
              {reservationsAvailable && (
                <Tag size="sm" startElement={<CalendarMonth />}>
                  Reservations available
                </Tag>
              )}
              {eventRateApplies && <EventRateTag eventRateWindow={eventRateWindow} />}
            </HStack>
          )}
          <HStack justify={detailsAction ? 'space-between' : 'flex-end'} align="center" gap="4">
            {detailsAction}
            {bookNow}
          </HStack>
        </Stack>
      </Box>
    );
  }

  const isCard = variant === 'card';
  const canOpenDetails = !!(onDetailsClick || detailsHref);
  const openDetails = () => {
    if (onDetailsClick) {
      onDetailsClick();
      return;
    }
    if (!detailsHref) return;
    if (detailsTarget === '_blank') {
      window.open(detailsHref, detailsTarget, 'noopener,noreferrer');
      return;
    }
    window.location.assign(detailsHref);
  };
  const mapPreviewUrls = isCard ? previewImageUrls(imageUrls, imageUrl) : [];

  return (
    <Box
      bg={background}
      h={isCard ? 'full' : undefined}
      {...(isCard
        ? {
            border: '1px solid',
            borderColor: 'stroke.divider.emphasized',
            borderRadius: '2xl',
            overflow: 'hidden',
          }
        : {
            borderBottomWidth: '1px',
            borderBottomStyle: 'solid',
            borderBottomColor: 'stroke.divider.default',
            pt: 5,
            pb: 5,
            px: 4,
            _hover: selected || focused ? undefined : { bg: 'fill.background.light-gray' },
          })}
      w="full"
      cursor={canOpenDetails ? 'pointer' : undefined}
      onClick={canOpenDetails ? openDetails : undefined}
      role={canOpenDetails ? 'button' : undefined}
      tabIndex={canOpenDetails ? 0 : undefined}
      onKeyDown={
        canOpenDetails
          ? (event) => {
              // Nested Details/Directions handle their own keyboard activation —
              // ignore bubbled events so Enter/Space on those controls is not stolen.
              if (event.target !== event.currentTarget) return;
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openDetails();
              }
            }
          : undefined
      }
    >
      <VStack gap="0" align="stretch" h={isCard ? 'full' : undefined}>
        {showImage && mapPreviewUrls.length > 0 && (
          <Box h="160px" w="full" flexShrink={0}>
            <HeroImageCarousel alt={imageAlt} urls={mapPreviewUrls} />
          </Box>
        )}
        <HStack
          gap="4"
          align="stretch"
          h={isCard ? 'full' : undefined}
          px={isCard ? 4 : 0}
          pt={isCard ? 4 : 0}
          pb={isCard ? 4 : 0}
        >
          {!isCard && showImage && (
            <Box
              flexShrink={0}
              w={IMAGE_SIZE}
              h={IMAGE_SIZE}
              borderRadius="md"
              overflow="hidden"
              position="relative"
            >
              <Image
                alt={imageAlt}
                src={listCardImageUrl(imageUrls, imageUrl) ?? LIST_ZONE_PLACEHOLDER_URL}
                w="full"
                h="full"
                objectFit="cover"
              />
              {imageTag != null ? (
                <Box position="absolute" bottom="0" left="0" right="0">
                  <LocationTag imageTag>{imageTag}</LocationTag>
                </Box>
              ) : null}
            </Box>
          )}

          <VStack
            flex={1}
            minW={0}
            align="stretch"
            gap={isCard ? '2' : '4'}
            justify={isCard ? undefined : 'space-between'}
          >
            {/* Outer card already pads (px/py 4) — avoid a second inset on this row. */}
            <HStack justify="space-between" align="flex-start" gap="4">
              <VStack align="flex-start" gap="1" flex={1} minW={0}>
                <Text variant="MainBodyMedium" color="text.primary" lineClamp={1}>
                  {locationName}
                </Text>
                {walkLabel ? (
                  <HStack gap="1" color="text.secondary" fontSize="sm">
                    <DirectionsWalk aria-hidden />
                    <Text variant="SmallBodyRegular">{walkLabel}</Text>
                  </HStack>
                ) : (
                  isOpen != null && (
                    <Text variant="SmallBodyMedium" color="text.discount">
                      {isOpen ? 'Open' : 'Closed'}
                      {isOpen && is24Hours && ' 24 hrs'}
                      {isOpen && !is24Hours && closingTime && (
                        <Text as="span" variant="SmallBodyRegular" color="text.secondary">
                          {' • Until '}
                          {closingTime}
                        </Text>
                      )}
                    </Text>
                  )
                )}
                {(reservationsAvailable || eventRateApplies) && (
                  <HStack gap="2" flexWrap="wrap" alignSelf="stretch" w="full" minW={0}>
                    {reservationsAvailable && (
                      <Tag size="sm" startElement={<CalendarMonth />}>
                        Reservations available
                      </Tag>
                    )}
                    {eventRateApplies && <EventRateTag eventRateWindow={eventRateWindow} />}
                  </HStack>
                )}
              </VStack>
              {(priceFormatted || rateLabel) && (
                <VStack align="flex-end" gap="1" flexShrink={0}>
                  {priceFormatted && (
                    <Text variant="MainBodyDemiBold" color="text.primary">
                      {priceFormatted}
                    </Text>
                  )}
                  {rateLabel && (
                    <Text variant="SmallBodyRegular" color="text.secondary">
                      {rateLabel}
                    </Text>
                  )}
                </VStack>
              )}
            </HStack>

            <HStack
              align="center"
              justify={isCard && !(detailsHref || onDetailsClick) ? 'flex-end' : 'space-between'}
              gap="4"
            >
              {!isCard && !onDetailsClick && !detailsHref && <Box />}
              {isCard && detailsHref && (
                <Button asChild variant="link" size="md">
                  <a
                    href={detailsHref}
                    target={detailsTarget}
                    rel={detailsTarget === '_blank' ? 'noopener noreferrer' : undefined}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {detailsLabel}
                  </a>
                </Button>
              )}
              {isCard && !detailsHref && onDetailsClick && (
                <Button
                  variant="link"
                  size="md"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDetailsClick();
                  }}
                >
                  {detailsLabel}
                </Button>
              )}
              {!isCard && detailsHref && (
                <Text asChild variant="Link">
                  <a
                    href={detailsHref}
                    target={detailsTarget}
                    rel={detailsTarget === '_blank' ? 'noopener noreferrer' : undefined}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {detailsLabel}
                  </a>
                </Text>
              )}
              {!isCard && !detailsHref && onDetailsClick && (
                <Text
                  as="button"
                  variant="Link"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDetailsClick();
                  }}
                >
                  {detailsLabel}
                </Text>
              )}
              {showDirections && (
                <HStack gap="2" flexShrink={0}>
                  {onCompactDirectionsClick && (
                    <Button
                      variant="secondary"
                      size="sm"
                      aria-label="Directions"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCompactDirectionsClick();
                      }}
                    >
                      <Directions />
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDirectionsClick?.();
                    }}
                  >
                    {directionsStartElement}
                    {directionsLabel}
                  </Button>
                </HStack>
              )}
            </HStack>
          </VStack>
        </HStack>
      </VStack>
    </Box>
  );
}

export interface ReservationCardCarouselItem extends ReservationCardProps {
  id: string;
}

export interface ReservationCardCarouselProps {
  items: ReservationCardCarouselItem[];
  onNext?: () => void;
  onPrevious?: () => void;
  title?: string;
}

export function ReservationCardCarousel({
  items,
  onNext: onNextProp,
  onPrevious: onPreviousProp,
  title = 'Explore more parking lots',
}: ReservationCardCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  // Track scroll position so we can hide Previous at the start and Next at the end.
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const updateEdges = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    const maxScroll = scrollWidth - clientWidth;
    setAtStart(scrollLeft <= 1);
    setAtEnd(scrollLeft >= maxScroll - 1);
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return undefined;
    updateEdges();
    container.addEventListener('scroll', updateEdges, { passive: true });
    const resizeObserver = new ResizeObserver(updateEdges);
    resizeObserver.observe(container);
    return () => {
      container.removeEventListener('scroll', updateEdges);
      resizeObserver.disconnect();
    };
  }, [updateEdges, items.length]);

  const scrollByCard = (direction: 1 | -1) => {
    const container = scrollRef.current;
    if (!container) return;
    const cards = Array.from(container.children) as HTMLElement[];
    if (cards.length === 0) return;
    const currentIdx = cards.reduce(
      (best, card, idx) =>
        Math.abs(card.offsetLeft - container.scrollLeft) <
        Math.abs(cards[best].offsetLeft - container.scrollLeft)
          ? idx
          : best,
      0,
    );
    const targetIdx = Math.max(0, Math.min(currentIdx + direction, cards.length - 1));
    container.scrollTo({ left: cards[targetIdx].offsetLeft, behavior: 'smooth' });
  };

  return (
    <Stack gap="0" bg="fill.background.default" w="full">
      <HStack justify="space-between" align="center" w="full">
        <SectionHeader title={title} padding={2} />
        <HStack gap={2} flexShrink={0} hideBelow="md">
          <Button
            variant="tertiary"
            size="sm"
            aria-label="Previous"
            disabled={atStart}
            onClick={() => (onPreviousProp ? onPreviousProp() : scrollByCard(-1))}
          >
            <ChevronLeft />
          </Button>
          <Button
            variant="tertiary"
            size="sm"
            aria-label="Next"
            disabled={atEnd}
            onClick={() => (onNextProp ? onNextProp() : scrollByCard(1))}
          >
            <ChevronRight />
          </Button>
        </HStack>
      </HStack>
      <Box
        ref={scrollRef}
        // position relative so the cards' offsetLeft is measured from this
        // container (not the page), keeping scrollByCard's snap targets correct.
        position="relative"
        display="flex"
        alignItems="stretch"
        gap="4"
        pt="4"
        pb="6"
        w="full"
        overflowX="auto"
        scrollSnapType="x mandatory"
        css={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
      >
        {items.map(({ id, onDetailsClick, detailsHref, ...cardProps }) => (
          <Box
            key={id}
            flexShrink={0}
            flexGrow={0}
            w={{ base: '321px', md: 'calc(33.33% - 22px)' }}
            maxW="321px"
            scrollSnapAlign="start"
          >
            <ReservationCard
              showImage={false}
              showDirections={false}
              variant="card"
              detailsHref={detailsHref}
              onDetailsClick={detailsHref ? undefined : (onDetailsClick ?? (() => {}))}
              {...cardProps}
            />
          </Box>
        ))}
      </Box>
    </Stack>
  );
}
